import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Music,
  Search,
  Send,
  Youtube,
  CheckCircle,
  Edit,
} from 'lucide-react';

import Navigation from '@/components/Navigation';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useStore } from '@/lib/store';
import * as api from '@/lib/api';
import {
  extractSpotifyTrackId,
  getSpotifyTrackFromInput,
  searchSpotifyTracks,
  type SimplifiedSpotifyTrack,
} from '@/lib/spotify';
import { suggestYoutubeVideo, type YoutubeVideoSuggestion } from '@/lib/youtube';
import type { Submission, League, Round } from '@/lib/mock-data';
import { extractYouTubeId, getYouTubeThumbnail } from '@/lib/utils';

type SubmissionProvider = NonNullable<Submission['providers']>[number];

const formatDuration = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const SubmitSong = () => {
  const { leagueId, roundId } = useParams<{ leagueId: string; roundId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { state, createSubmission, updateSubmission, getSubmissionByUserAndRound, getSubmissionsByRound, updateLeague, updateRound, addSubmissionsToState } = useStore();
  const { currentUser } = state;

  const [formData, setFormData] = useState({
    songTitle: '',
    artistName: '',
    comment: '',
  });

  const [spotifyQuery, setSpotifyQuery] = useState('');
  const [spotifyResults, setSpotifyResults] = useState<SimplifiedSpotifyTrack[]>([]);
  const [selectedSpotifyTrack, setSelectedSpotifyTrack] = useState<SimplifiedSpotifyTrack | null>(null);
  const [isSearchingSpotify, setIsSearchingSpotify] = useState(false);
  const [spotifyError, setSpotifyError] = useState<string | null>(null);

  const [youtubeSuggestion, setYoutubeSuggestion] = useState<YoutubeVideoSuggestion | null>(null);
  const [selectedYoutube, setSelectedYoutube] = useState<YoutubeVideoSuggestion | null>(null);
  const [isSearchingYoutube, setIsSearchingYoutube] = useState(false);
  const [youtubeError, setYoutubeError] = useState<string | null>(null);
  const [manualYoutubeUrl, setManualYoutubeUrl] = useState('');
  const [showYoutubeFallback, setShowYoutubeFallback] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [fetchedLeague, setFetchedLeague] = useState<League | null>(null);
  const [fetchedRound, setFetchedRound] = useState<Round | null>(null);

  // Try to find league/round in state first
  const leagueFromState = useMemo(() => state.leagues.find((l) => l.id === leagueId), [state.leagues, leagueId]);
  const roundFromState = useMemo(() => state.rounds.find((r) => r.id === roundId), [state.rounds, roundId]);
  
  // Use fetched data if not in state
  const league = leagueFromState || fetchedLeague;
  // Always prefer round from state to ensure we have the latest status
  const round = roundFromState || fetchedRound;
  
  // Debug logging
  useEffect(() => {
    if (round) {
      console.log('[SubmitSong] Round status:', round.status, 'roundId:', round.id);
    }
  }, [round]);

  // Track if we've already refreshed to prevent infinite loops
  const hasRefreshedRef = useRef(false);
  
  // Fetch league, round, and submissions from Supabase if not in state
  // Also refresh round from API once on mount to ensure we have latest status
  useEffect(() => {
    if (!leagueId || !roundId) return;
    
    // Check if we need to fetch
    const needsLeague = !leagueFromState;
    const needsRound = !roundFromState;
    
    // Refresh round from API once on mount to get latest status (in case it was updated)
    const refreshRound = async () => {
      if (hasRefreshedRef.current) return; // Only refresh once
      
      const isSupabaseConfigured = () => {
        const url = import.meta.env.VITE_SUPABASE_URL;
        const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
        return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
      };
      
      if (isSupabaseConfigured() && roundId) {
        try {
          const latestRound = await api.roundAPI.getRound(roundId);
          if (latestRound) {
            updateRound(latestRound.id, latestRound);
            console.log('[SubmitSong] Refreshed round from API, status:', latestRound.status);
            hasRefreshedRef.current = true;
          }
        } catch (error) {
          console.error('[SubmitSong] Failed to refresh round:', error);
        }
      }
    };
    
    if (!needsLeague && !needsRound) {
      // Still refresh once to get latest status
      refreshRound();
      return;
    }
    
    const fetchData = async () => {
      setIsLoadingData(true);
      try {
        // Check if Supabase is configured
        const isSupabaseConfigured = () => {
          const url = import.meta.env.VITE_SUPABASE_URL;
          const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
          return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
        };
        
        if (isSupabaseConfigured()) {
          // Fetch league if needed
          if (needsLeague) {
            const fetched = await api.leagueAPI.getLeague(leagueId);
            if (fetched) {
              setFetchedLeague(fetched);
              // Also add to state by updating the league (which will add it if it doesn't exist)
              updateLeague(fetched.id, fetched);
            }
          }
          
          // Fetch round if needed
          if (needsRound) {
            const fetched = await api.roundAPI.getRound(roundId);
            if (fetched) {
              setFetchedRound(fetched);
              // Also add to state by updating the round (which will add it if it doesn't exist)
              updateRound(fetched.id, fetched);
            }
          }
          
          // Always fetch submissions for this round to ensure we have the latest data
          const submissions = await api.submissionAPI.getSubmissionsByRound(roundId);
          if (submissions.length > 0) {
            // Update state with fetched submissions
            addSubmissionsToState(submissions);
          }
        }
      } catch (error) {
        console.error('Failed to fetch league/round/submissions:', error);
        toast({
          title: 'Error',
          description: 'Failed to load league or round data.',
          variant: 'destructive',
        });
      } finally {
        setIsLoadingData(false);
      }
    };
    
    fetchData();
  }, [leagueId, roundId, leagueFromState, roundFromState, toast, updateLeague, updateRound]);

  const existingSubmission = useMemo(() => {
    if (!roundId || !currentUser?.id) return undefined;
    return getSubmissionByUserAndRound(currentUser.id, roundId);
  }, [roundId, currentUser?.id, getSubmissionByUserAndRound, state.submissions]);

  // Load existing submission data into form
  useEffect(() => {
    if (existingSubmission) {
      setFormData({
        songTitle: existingSubmission.song_title,
        artistName: existingSubmission.artist_name,
        comment: existingSubmission.submitter_comment || '',
      });
      
      // Load Spotify track if available
      if (existingSubmission.spotify_track_id && existingSubmission.spotify_url) {
        // We'll need to reconstruct the track from submission data
        const track: SimplifiedSpotifyTrack = {
          id: existingSubmission.spotify_track_id,
          name: existingSubmission.song_title,
          artists: existingSubmission.spotify_artists || [existingSubmission.artist_name],
          albumName: existingSubmission.spotify_album || '',
          albumImage: existingSubmission.spotify_album_image,
          durationMs: 0, // Not stored in submission
          previewUrl: existingSubmission.spotify_preview_url,
          externalUrl: existingSubmission.spotify_url,
          isrc: existingSubmission.spotify_isrc,
        };
        setSelectedSpotifyTrack(track);
      }
      
      // Load YouTube if available
      if (existingSubmission.youtube_video_id && existingSubmission.youtube_url) {
        const youtube: YoutubeVideoSuggestion = {
          id: existingSubmission.youtube_video_id,
          title: existingSubmission.song_title,
          url: existingSubmission.youtube_url,
          thumbnail: existingSubmission.video_thumbnail,
          channel: existingSubmission.artist_name,
        };
        setSelectedYoutube(youtube);
        setShowYoutubeFallback(true); // Show YouTube section if there's an existing YouTube submission
      }
    }
  }, [existingSubmission]);

  const missingRound = !league || !round;
  const requireSpotify = league?.require_spotify ?? false;
  const allowYoutubeFallback = !requireSpotify;
  
  // Check if Spotify API is available (has token proxy or client credentials)
  const spotifyApiAvailable = Boolean(
    import.meta.env.VITE_SPOTIFY_TOKEN_PROXY_URL || 
    (import.meta.env.VITE_SPOTIFY_CLIENT_ID && import.meta.env.VITE_SPOTIFY_CLIENT_SECRET)
  );

  const getTimeRemaining = () => {
    if (isDeadlinePassed) return 'Deadline has passed';
    const diff = deadline.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} and ${hours} hour${hours !== 1 ? 's' : ''} remaining`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes !== 1 ? 's' : ''} remaining`;
    return `${minutes} minute${minutes !== 1 ? 's' : ''} remaining`;
  };

  useEffect(() => {
    if (missingRound) {
      setSpotifyResults([]);
      setSpotifyError(null);
      return;
    }

    let cancelled = false;

    if (!spotifyQuery.trim()) {
      setSpotifyResults([]);
      setSpotifyError(null);
      if (!selectedSpotifyTrack) {
        setYoutubeSuggestion(null);
        setSelectedYoutube(null);
      }
      return;
    }

    const handleSearch = async (): Promise<(() => void) | undefined> => {
      const trimmed = spotifyQuery.trim();
      const embeddedId = extractSpotifyTrackId(trimmed);

      if (embeddedId) {
        setIsSearchingSpotify(true);
        try {
          const track = await getSpotifyTrackFromInput(trimmed);
          if (cancelled) return;
          if (track) {
            setSpotifyResults([track]);
            setSpotifyError(null);
          } else {
            setSpotifyResults([]);
            setSpotifyError('Track not found on Spotify.');
          }
        } catch (error) {
          console.error(error);
          if (!cancelled) {
            setSpotifyResults([]);
            setSpotifyError('Unable to look up that Spotify track.');
          }
        } finally {
          if (!cancelled) {
            setIsSearchingSpotify(false);
          }
        }
        return undefined;
      }

      setIsSearchingSpotify(true);
      const timeout = setTimeout(async () => {
        try {
          const results = await searchSpotifyTracks(trimmed);
          if (cancelled) return;
          setSpotifyResults(results);
          setSpotifyError(results.length ? null : 'No Spotify matches found.');
        } catch (error) {
          console.error(error);
          if (!cancelled) {
            setSpotifyResults([]);
            setSpotifyError('Unable to search Spotify right now.');
          }
        } finally {
          if (!cancelled) {
            setIsSearchingSpotify(false);
          }
        }
      }, 400);

      return () => clearTimeout(timeout);
    };

    let cleanupFn: (() => void) | undefined;
    handleSearch().then((cleanup) => {
      cleanupFn = cleanup;
    });
    
    return () => {
      cancelled = true;
      if (cleanupFn) {
        cleanupFn();
      }
    };
  }, [spotifyQuery, selectedSpotifyTrack, missingRound]);

  // Auto-show YouTube fallback if Spotify API is not available
  useEffect(() => {
    if (!spotifyApiAvailable && !requireSpotify && allowYoutubeFallback && !showYoutubeFallback) {
      setShowYoutubeFallback(true);
    }
  }, [spotifyApiAvailable, requireSpotify, allowYoutubeFallback, showYoutubeFallback]);

  useEffect(() => {
    // Only auto-suggest YouTube if checkbox is checked
    if (!selectedSpotifyTrack || !allowYoutubeFallback || missingRound || !showYoutubeFallback) return;

    setFormData((prev) => ({
      ...prev,
      songTitle: prev.songTitle || selectedSpotifyTrack.name,
      artistName: prev.artistName || selectedSpotifyTrack.artists.join(', '),
    }));

    const lookup = async () => {
      setIsSearchingYoutube(true);
      try {
        const query = `${selectedSpotifyTrack.name} ${selectedSpotifyTrack.artists[0] ?? ''}`;
        const result = await suggestYoutubeVideo(query);
        setYoutubeSuggestion(result);
        setYoutubeError(null);
      } catch (error) {
        console.error(error);
        setYoutubeSuggestion(null);
        setYoutubeError('Unable to fetch a YouTube match automatically.');
      } finally {
        setIsSearchingYoutube(false);
      }
    };

    lookup();
  }, [selectedSpotifyTrack, allowYoutubeFallback, missingRound, showYoutubeFallback]);

  const deadline = round ? new Date(round.submission_deadline) : new Date();
  const now = new Date();
  const isDeadlinePassed = round ? now > deadline : false;
  const isNotSubmittingStatus = round ? round.status !== 'submitting' : false;
  
  // Check if all members have submitted
  const allSubmissions = useMemo(() => {
    if (!roundId) return [];
    return getSubmissionsByRound(roundId);
  }, [roundId, getSubmissionsByRound, state.submissions]);
  
  const allPlayersSubmitted = league ? allSubmissions.length >= league.max_players : false;
  
  // Can edit if: round is in submitting status AND (deadline hasn't passed OR round was reopened) AND not all members have submitted
  // If status is 'submitting', allow submissions even if deadline passed (admin may have reopened)
  const canEdit = round?.status === 'submitting' && !allPlayersSubmitted;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!formData.songTitle.trim() || !formData.artistName.trim()) {
      toast({
        title: 'Missing information',
        description: 'Please provide both song title and artist name.',
        variant: 'destructive',
      });
      return;
    }

    if (!league || !round) {
      toast({
        title: 'Error',
        description: 'League or round not found.',
        variant: 'destructive',
      });
      return;
    }

    if (!currentUser) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in again to submit a song.',
        variant: 'destructive',
      });
      return;
    }

    // Use the canEdit calculated earlier
    
    if (existingSubmission && !canEdit) {
      toast({
        title: 'Cannot edit submission',
        description: isDeadlinePassed 
          ? 'The submission deadline has passed.'
          : 'All members have submitted, so editing is no longer allowed.',
        variant: 'destructive',
      });
      return;
    }

    const spotifyTrack = selectedSpotifyTrack;
    const youtube = selectedYoutube;

    if (!spotifyTrack && requireSpotify) {
      toast({
        title: 'Spotify required',
        description: 'This league only accepts songs that are available on Spotify.',
        variant: 'destructive',
      });
      return;
    }

    // If Spotify is not required and API is not available, YouTube becomes mandatory
    if (!spotifyTrack && !youtube) {
      if (!requireSpotify && !spotifyApiAvailable) {
        toast({
          title: 'YouTube link required',
          description: 'Please provide a YouTube link for your song since Spotify API is not available.',
          variant: 'destructive',
        });
        return;
      }
      toast({
        title: 'Select a track',
        description: 'Pick a Spotify track or provide a YouTube link before submitting.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const providers: SubmissionProvider[] = [];
      if (spotifyTrack) {
        providers.push({
          provider: 'spotify',
          url: spotifyTrack.externalUrl,
          id: spotifyTrack.id,
        });
      }
      if (youtube) {
        providers.push({
          provider: 'youtube',
          url: youtube.url,
          id: youtube.id,
        });
      }

      const submissionData = {
        round_id: round.id,
        user_id: currentUser.id,
        username: currentUser.username,
        song_title: formData.songTitle.trim(),
        artist_name: formData.artistName.trim(),
        submitter_comment: formData.comment.trim() || undefined,
        video_url: youtube?.url,
        video_thumbnail:
          youtube?.thumbnail ??
          (youtube?.id ? getYouTubeThumbnail(youtube.id, 'mqdefault') : undefined) ??
          spotifyTrack?.albumImage,
        spotify_track_id: spotifyTrack?.id,
        spotify_url: spotifyTrack?.externalUrl,
        spotify_preview_url: spotifyTrack?.previewUrl ?? undefined,
        spotify_album: spotifyTrack?.albumName,
        spotify_album_image: spotifyTrack?.albumImage,
        spotify_artists: spotifyTrack?.artists,
        spotify_isrc: spotifyTrack?.isrc,
        youtube_video_id: youtube?.id,
        youtube_url: youtube?.url,
        providers,
        submission_source: (spotifyTrack
          ? youtube
            ? 'spotify_with_youtube'
            : 'spotify'
          : 'youtube_only') as 'spotify' | 'spotify_with_youtube' | 'youtube_only' | 'manual',
      };

      // Check if submission exists (in state or try to find it)
      let submissionToUpdate = existingSubmission;
      
      // If we don't have it in state but we're using Supabase, try to find it
      if (!submissionToUpdate && currentUser?.id && roundId) {
        try {
          const submissions = await api.submissionAPI.getSubmissionsByRound(roundId);
          submissionToUpdate = submissions.find(s => s.user_id === currentUser.id);
          // If found, add it to state
          if (submissionToUpdate) {
            addSubmissionsToState([submissionToUpdate]);
          }
        } catch (error) {
          console.error('Error checking for existing submission:', error);
        }
      }
      
      if (submissionToUpdate && canEdit) {
        // Update existing submission
        await updateSubmission(submissionToUpdate.id, submissionData);
        toast({
          title: 'Submission updated! 🎵',
          description: `Your submission "${formData.songTitle}" by ${formData.artistName} has been updated successfully.`,
          duration: 5000,
        });
        // Don't navigate away - let them see the updated submission
        // The component will re-render with the updated submission
      } else if (submissionToUpdate && !canEdit) {
        // Submission exists but can't edit
        toast({
          title: 'Cannot update',
          description: 'Your submission is locked and cannot be edited.',
          variant: 'destructive',
        });
      } else {
        // Create new submission - but handle duplicate key error
        try {
          await createSubmission(submissionData);
          toast({
            title: 'Song submitted! 🎵',
            description: `Your submission "${formData.songTitle}" by ${formData.artistName} has been submitted successfully.`,
            duration: 5000,
          });
          // Don't navigate away immediately - show success state
          // Small delay then navigate
          setTimeout(() => {
            navigate(`/leagues/${leagueId}`);
          }, 2000);
          return; // Exit early to show success state
        } catch (createError: any) {
          // If we get a duplicate key error, it means a submission already exists
          // Try to fetch it and update instead
          if (createError.message?.includes('duplicate key') || createError.message?.includes('unique constraint')) {
            console.log('Duplicate submission detected, fetching existing submission to update...');
            try {
              const submissions = await api.submissionAPI.getSubmissionsByRound(roundId);
              const existing = submissions.find(s => s.user_id === currentUser?.id);
              if (existing && canEdit) {
                await updateSubmission(existing.id, submissionData);
                toast({
                  title: 'Submission updated! 🎵',
                  description: `Your submission "${formData.songTitle}" by ${formData.artistName} has been updated successfully.`,
                  duration: 5000,
                });
              } else {
                throw createError; // Re-throw if we can't update
              }
            } catch (updateError) {
              // If update also fails, show the original error
              throw createError;
            }
          } else {
            throw createError;
          }
        }
      }
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error',
        description: 'Failed to submit song. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormChange = (field: 'songTitle' | 'artistName' | 'comment', value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Submission is locked if: not in submitting status OR (has submission AND cannot edit)
  // Note: We allow submissions even if deadline passed if status is 'submitting' (admin may have reopened)
  // IMPORTANT: Only lock if status is explicitly NOT 'submitting'
  const isSubmissionLocked =
    (round && round.status !== 'submitting') || (existingSubmission && !canEdit);
  const hasCoreInfo = Boolean(formData.songTitle.trim() && formData.artistName.trim());
  // Submission is ready if:
  // 1. Spotify track is selected, OR
  // 2. YouTube is selected (when YouTube fallback is allowed)
  const submissionReady =
    Boolean(selectedSpotifyTrack) || 
    (allowYoutubeFallback && Boolean(selectedYoutube));
  const canSubmit = !isSubmissionLocked && hasCoreInfo && submissionReady;

  // Show loading state while fetching data
  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-muted/20 to-background">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading league and round data...</p>
        </div>
      </div>
    );
  }

  if (missingRound) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-muted/20 to-background">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-4">Not found</h1>
          <p className="text-muted-foreground mb-6">
            The league or round you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/20 to-background">
      <Navigation />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(`/leagues/${leagueId}`)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to League
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {existingSubmission ? 'Edit Your Submission' : 'Submit Your Song'}
              </h1>
              <p className="text-muted-foreground">
                {round.theme}
              </p>
            </div>
            {existingSubmission && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                <span className="text-sm font-semibold text-green-700 dark:text-green-300">
                  Submission Received
                </span>
              </div>
            )}
          </div>
        </div>

        <Card className="mb-6 border-2 border-primary/20 bg-gradient-to-br from-purple-50/30 to-blue-50/30 dark:from-purple-950/15 dark:to-blue-950/15 shadow-md">
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                  Theme
                </Label>
                <p className="text-lg font-semibold text-foreground mt-1">{round.theme}</p>
              </div>
              {round.description && (
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                    Description
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">{round.description}</p>
                </div>
              )}
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                  Submission Deadline
                </Label>
                <p
                  className={`text-sm font-medium mt-1 ${
                    isDeadlinePassed ? 'text-destructive' : 'text-foreground'
                  }`}
                >
                  {deadline.toLocaleString()} ({getTimeRemaining()})
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Only show deadline passed alert if status is NOT submitting */}
        {isDeadlinePassed && round?.status === 'submitting' && (
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              The submission deadline has passed, but submissions have been reopened. You can still submit a song for this round.
            </AlertDescription>
          </Alert>
        )}
        {isDeadlinePassed && round?.status !== 'submitting' && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              The submission deadline has passed. You can no longer submit a song for this round.
            </AlertDescription>
          </Alert>
        )}

        {/* Only show status error if status is NOT submitting */}
        {isNotSubmittingStatus && round?.status !== 'submitting' && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This round is no longer accepting submissions. The current status is: {round?.status || 'unknown'}.
            </AlertDescription>
          </Alert>
        )}

        {existingSubmission && (
          <Alert className={`mb-6 ${canEdit ? 'border-green-500 bg-green-50/50 dark:bg-green-900/20' : 'border-amber-500 bg-amber-50/50 dark:bg-amber-900/20'}`}>
            <div className="flex items-start gap-3">
              {canEdit ? (
                <Edit className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
              )}
              <div className="flex-1">
                <AlertDescription className="text-base">
                  {canEdit ? (
                    <div className="space-y-2">
                      <p className="font-semibold text-green-900 dark:text-green-100">
                        ✓ Your submission has been received!
                      </p>
                      <p className="text-green-800 dark:text-green-200">
                        You submitted <strong>"{existingSubmission.song_title}"</strong> by{' '}
                        <strong>{existingSubmission.artist_name}</strong>.
                      </p>
                      <p className="text-sm text-green-700 dark:text-green-300">
                        {round?.status === 'submitting' && isDeadlinePassed 
                          ? 'Submissions have been reopened. You can edit your submission until all players have submitted.'
                          : isDeadlinePassed 
                            ? 'The deadline has passed'
                            : allPlayersSubmitted 
                              ? 'All players have submitted'
                              : `You can edit your submission until the deadline (${deadline.toLocaleString()}) or until all ${league?.max_players || 0} players have submitted`}.
                      </p>
                      <p className="text-sm font-medium text-green-800 dark:text-green-200 mt-2">
                        {allPlayersSubmitted 
                          ? `All ${league?.max_players || 0} players have submitted. Editing is now locked.`
                          : `${allSubmissions.length} of ${league?.max_players || 0} players have submitted.`
                        }
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="font-semibold text-amber-900 dark:text-amber-100">
                        ✓ Submission Locked
                      </p>
                      <p className="text-amber-800 dark:text-amber-200">
                        You submitted <strong>"{existingSubmission.song_title}"</strong> by{' '}
                        <strong>{existingSubmission.artist_name}</strong> for this round.
                      </p>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        {round?.status === 'submitting' && isDeadlinePassed
                          ? 'Submissions have been reopened. You can edit your submission until all players have submitted.'
                          : isDeadlinePassed 
                            ? 'The submission deadline has passed, so editing is no longer available.'
                            : allPlayersSubmitted
                              ? `All ${league?.max_players || 0} players have submitted, so editing is now locked.`
                              : 'Editing is no longer available for this round.'}
                      </p>
                    </div>
                  )}
                </AlertDescription>
              </div>
            </div>
          </Alert>
        )}

        <Card className="card-shadow border-2 border-primary/30 bg-gradient-to-br from-purple-100/40 to-blue-100/40 dark:from-purple-950/20 dark:to-blue-950/20 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary/15 to-blue-500/15 dark:from-primary/20 dark:to-blue-400/20 rounded-t-lg border-b border-primary/30">
            <CardTitle className="flex items-center text-primary">
              <Music className="h-5 w-5 mr-2" />
              Song Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {(!requireSpotify || spotifyApiAvailable) && (
                <div className="space-y-2">
                  <Label htmlFor="spotifySearch">
                    Spotify track {requireSpotify ? '*' : '(optional)'}
                    {!spotifyApiAvailable && !requireSpotify && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        (API not configured - you can skip this)
                      </span>
                    )}
                  </Label>
                  <Input
                    id="spotifySearch"
                    placeholder={spotifyApiAvailable 
                      ? "Search by title, artist, or paste a Spotify link"
                      : "Spotify API not available - use YouTube option below instead"
                    }
                    value={spotifyQuery}
                    onChange={(event) => {
                      setSpotifyQuery(event.target.value);
                      setSelectedSpotifyTrack(null);
                      setSelectedYoutube(null);
                      setYoutubeSuggestion(null);
                    }}
                    disabled={isSubmissionLocked || isSubmitting || (!spotifyApiAvailable && !requireSpotify)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {spotifyApiAvailable 
                      ? "Start typing to search Spotify. Paste the full Spotify URL if you already have it."
                      : "Spotify search is not available. Please use the YouTube option below to submit your song."
                    }
                  </p>
                </div>
              )}

              {isSearchingSpotify && (
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Searching Spotify…</span>
                </div>
              )}

              {spotifyError && !isSearchingSpotify && (
                <p className="text-sm text-destructive">{spotifyError}</p>
              )}

              {!selectedSpotifyTrack && spotifyResults.length > 0 && (
                <div className="space-y-2 rounded-lg border p-3">
                  {spotifyResults.map((track) => (
                    <button
                      type="button"
                      key={track.id}
                      className="flex w-full items-center justify-between rounded-md border border-border px-3 py-2 text-left transition hover:border-primary/60 hover:bg-accent"
                      onClick={() => {
                        setSelectedSpotifyTrack(track);
                        setSpotifyResults([]);
                        setSpotifyQuery(`${track.name} – ${track.artists[0] ?? ''}`);
                        setFormData((prev) => ({
                          ...prev,
                          songTitle: track.name,
                          artistName: track.artists.join(', '),
                        }));
                      }}
                    >
                      <div>
                        <p className="font-medium text-sm text-foreground">{track.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {track.artists.join(', ')} • {track.albumName}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDuration(track.durationMs)}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {selectedSpotifyTrack && (
                <div className="rounded-lg border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {selectedSpotifyTrack.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {selectedSpotifyTrack.artists.join(', ')} • {selectedSpotifyTrack.albumName}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={selectedSpotifyTrack.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs text-muted-foreground hover:text-primary"
                      >
                        Open in Spotify
                        <ExternalLink className="ml-1 h-3 w-3" />
                      </a>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedSpotifyTrack(null);
                          setSpotifyResults([]);
                          setSpotifyQuery('');
                          setSelectedYoutube(null);
                          setYoutubeSuggestion(null);
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="songTitle">Song Title *</Label>
                <Input
                  id="songTitle"
                  placeholder="e.g., Bohemian Rhapsody"
                  value={formData.songTitle}
                  onChange={(event) => handleFormChange('songTitle', event.target.value)}
                  disabled={isSubmissionLocked || isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Enter the exact title of the song as it appears on the recording.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="artistName">Artist Name *</Label>
                <Input
                  id="artistName"
                  placeholder="e.g., Queen"
                  value={formData.artistName}
                  onChange={(event) => handleFormChange('artistName', event.target.value)}
                  disabled={isSubmissionLocked || isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Enter the primary artist or band name.
                </p>
              </div>

              {allowYoutubeFallback && (
                <div className="space-y-3">
                  {!spotifyApiAvailable && (
                    <Alert className="border-blue-500 bg-blue-50/50 dark:bg-blue-900/10">
                      <Youtube className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-sm">
                        <strong>No Spotify API configured.</strong> Please provide a YouTube link below to submit your song. 
                        You can search for the song on YouTube and paste the link, or use the manual search.
                      </AlertDescription>
                    </Alert>
                  )}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="showYoutubeFallback"
                      checked={showYoutubeFallback || (!spotifyApiAvailable && !requireSpotify)}
                      onCheckedChange={(checked) => {
                        setShowYoutubeFallback(checked === true);
                        if (!checked && spotifyApiAvailable) {
                          // Clear YouTube selection when unchecked (only if Spotify API is available)
                          setSelectedYoutube(null);
                          setYoutubeSuggestion(null);
                          setManualYoutubeUrl('');
                        }
                      }}
                      disabled={isSubmissionLocked || isSubmitting || (!spotifyApiAvailable && !requireSpotify)}
                    />
                    <Label
                      htmlFor="showYoutubeFallback"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {spotifyApiAvailable 
                        ? "Can't find your song on Spotify? Find it on YouTube instead"
                        : "Provide a YouTube link for your song *"
                      }
                    </Label>
                  </div>
                </div>
              )}

              {showYoutubeFallback && allowYoutubeFallback && (
                <div className="space-y-3 rounded-lg border border-dashed p-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      YouTube Link {!spotifyApiAvailable && !requireSpotify ? '*' : '(optional)'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {!spotifyApiAvailable && !requireSpotify
                        ? "Provide a YouTube link so voters can listen to your submission."
                        : "We'll try to find a YouTube match so every voter can listen."
                      }
                    </p>
                  </div>
                  <Youtube className="h-5 w-5 text-muted-foreground" />
                </div>

                {isSearchingYoutube && (
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Searching YouTube…</span>
                  </div>
                )}

                {youtubeError && (
                  <p className="text-xs text-destructive">{youtubeError}</p>
                )}

                {youtubeSuggestion && (
                  <div className="flex items-start gap-3 rounded-md border p-3">
                    <img
                      src={
                        youtubeSuggestion.thumbnail ||
                        getYouTubeThumbnail(youtubeSuggestion.id, 'mqdefault')
                      }
                      alt={youtubeSuggestion.title}
                      className="h-16 w-28 rounded object-cover"
                    />
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-foreground">{youtubeSuggestion.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {youtubeSuggestion.channel || 'YouTube'}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        <Button
                          type="button"
                          size="sm"
                          className="btn-music"
                          onClick={() => {
                            setSelectedYoutube(youtubeSuggestion);
                            setManualYoutubeUrl(youtubeSuggestion.url);
                            toast({
                              title: 'YouTube fallback saved',
                              description: 'This video will be embedded for playback.',
                            });
                          }}
                        >
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          Use this video
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedYoutube(null);
                            toast({
                              title: 'YouTube fallback cleared',
                              description: 'You can search again or paste a different link.',
                            });
                          }}
                        >
                          Dismiss
                        </Button>
                        <a
                          className="inline-flex items-center text-xs text-muted-foreground hover:text-primary"
                          href={youtubeSuggestion.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Preview on YouTube
                          <ExternalLink className="ml-1 h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          if (!formData.songTitle || !formData.artistName) {
                            toast({
                              title: 'Add song details first',
                              description: 'Provide a title and artist to search YouTube.',
                              variant: 'destructive',
                            });
                            return;
                          }
                          setIsSearchingYoutube(true);
                          try {
                            const query = `${formData.songTitle} ${formData.artistName}`;
                            const result = await suggestYoutubeVideo(query);
                            setYoutubeSuggestion(result);
                            if (result) {
                              setYoutubeError(null);
                              toast({
                                title: 'YouTube match found',
                                description: result.title,
                              });
                            } else {
                              setYoutubeError('No good YouTube match found.');
                            }
                          } catch (error) {
                            console.error(error);
                            setYoutubeError('Unable to search YouTube right now.');
                          } finally {
                            setIsSearchingYoutube(false);
                          }
                        }}
                      >
                        <Search className="mr-2 h-4 w-4" />
                        Search YouTube manually
                      </Button>
                      <p className="text-xs text-muted-foreground">
                        We’ll use the song title and artist to look for a video.
                      </p>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="manualYoutube">Or paste a YouTube link</Label>
                      <Input
                        id="manualYoutube"
                        placeholder="https://www.youtube.com/watch?v=..."
                        value={manualYoutubeUrl}
                        onChange={(event) => setManualYoutubeUrl(event.target.value)}
                        disabled={isSubmitting || isSubmissionLocked}
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const id = extractYouTubeId(manualYoutubeUrl);
                            if (!id) {
                              toast({
                                title: 'Invalid YouTube link',
                                description: 'Please check the URL and try again.',
                                variant: 'destructive',
                              });
                              return;
                            }
                            const suggestion: YoutubeVideoSuggestion = {
                              id,
                              url: `https://www.youtube.com/watch?v=${id}`,
                              title: formData.songTitle || 'YouTube submission',
                              thumbnail: getYouTubeThumbnail(id, 'mqdefault'),
                              channel: '',
                            };
                            setSelectedYoutube(suggestion);
                            setYoutubeSuggestion(suggestion);
                            toast({
                              title: 'YouTube fallback saved',
                              description: 'This link will be used for playback.',
                            });
                          }}
                        >
                          Save link
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setManualYoutubeUrl('');
                            setSelectedYoutube(null);
                          }}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="comment">Why This Song? (Optional)</Label>
                <Textarea
                  id="comment"
                  placeholder="Explain why this song fits the theme, or share what makes it special..."
                  rows={4}
                  value={formData.comment}
                  onChange={(event) => handleFormChange('comment', event.target.value)}
                  disabled={isSubmissionLocked || isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Help others understand your choice and why it deserves votes.
                </p>
              </div>

              {selectedYoutube && (
                <div className="rounded-lg border border-muted bg-muted/40 p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                    Playback preview
                  </p>
                  <AspectRatio ratio={16 / 9} className="overflow-hidden rounded-lg">
                    <iframe
                      src={`https://www.youtube.com/embed/${selectedYoutube.id}`}
                      title="Submission preview"
                      className="h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </AspectRatio>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/leagues/${leagueId}`)}
                  disabled={isSubmitting}
                >
                  {existingSubmission ? 'Back to League' : 'Cancel'}
                </Button>
                <Button 
                  type="submit" 
                  className={`flex-1 ${existingSubmission ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'btn-music'}`}
                  disabled={!canSubmit || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      {existingSubmission ? 'Updating...' : 'Submitting...'}
                    </>
                  ) : (
                    <>
                      {existingSubmission ? (
                        <>
                          <Edit className="h-4 w-4 mr-2" />
                          Update Submission
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Submit Song
                        </>
                      )}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="mt-6 border-2 border-secondary/30 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 dark:from-blue-950/15 dark:to-indigo-950/15 shadow-md">
          <CardHeader className="bg-gradient-to-r from-blue-500/15 to-indigo-500/15 dark:from-blue-400/20 dark:to-indigo-400/20 rounded-t-lg border-b border-secondary/30">
            <CardTitle className="text-blue-600 dark:text-blue-400">Submission Guidelines</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Make sure your song clearly fits the theme for this round.</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  Provide accurate song and artist information to help others find and listen to your submission.
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>You can only submit one song per round.</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>
                  You can edit your submission until the deadline passes or until all members have submitted.
                </span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Be creative with your comment – it might help you get votes!</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default SubmitSong;

